// Minimal markdown parser + renderers. Editor-authored narratives (release
// + feature-launch copy shown on Timeline cards) use a narrow markdown
// subset — headings, bold, italic, unordered lists, paragraphs, links,
// inline code — so a hand-rolled parser covers the space without a dep.
//
// Two renderers over a shared parse step:
//   markdownToReact(md)  → React nodes, for the Timeline cards (no
//                          innerHTML, no XSS surface).
//   markdownToHtml(md)   → string of HTML, for rich clipboard writes.
//
// This is NOT a general-purpose markdown renderer. Doesn't handle:
//   - tables, blockquotes, code fences, HR rules
//   - nested lists (single-level only)
//   - reference-style links, image syntax
//
// If the LLM starts emitting more complex markdown, switch to `marked`.
import { Fragment, type ReactNode } from "react";
import { BARE_URL_SOURCE, linkLabel, trimUrl } from "./links";

// ─── Parse ───────────────────────────────────────────────────────────

type Block =
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  // `lines` preserves author line breaks: a single newline inside a
  // paragraph renders as a hard break (<br>), not a collapsed space. This
  // deviates from CommonMark on purpose — narratives are typed in plain
  // textareas where a return is meant to be a return.
  | { kind: "para"; lines: string[] }
  | { kind: "list"; items: string[] };

function parseBlocks(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out: Block[] = [];
  let listBuffer: string[] | null = null;
  let paraBuffer: string[] | null = null;

  const flushList = () => {
    if (listBuffer) {
      out.push({ kind: "list", items: listBuffer });
      listBuffer = null;
    }
  };
  const flushPara = () => {
    if (paraBuffer && paraBuffer.length > 0) {
      out.push({ kind: "para", lines: paraBuffer });
      paraBuffer = null;
    }
  };
  const flushAll = () => {
    flushList();
    flushPara();
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === "") {
      flushAll();
      continue;
    }
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      flushAll();
      const level = heading[1]!.length as 1 | 2 | 3;
      out.push({ kind: "heading", level, text: heading[2]! });
      continue;
    }
    const bullet = /^[-*]\s+(.+)$/.exec(line);
    if (bullet) {
      flushPara();
      if (!listBuffer) listBuffer = [];
      listBuffer.push(bullet[1]!);
      continue;
    }
    flushList();
    if (!paraBuffer) paraBuffer = [];
    paraBuffer.push(line);
  }
  flushAll();
  return out;
}

// ─── Inline tokenization ─────────────────────────────────────────────
//
// Bold/italic/code/link, plus bare http(s) URLs autolinked with a short
// label (see lib/links.ts) since authors paste raw URLs. Non-nested — the
// LLM doesn't emit **_mixed_** spans and we don't try to handle them. Regex is deliberately anchored
// with lookarounds so `**word**` parses as bold rather than as two
// italic asterisks flanking `word*`.

type Inline =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string }
  | { kind: "code"; text: string }
  | { kind: "link"; text: string; href: string };

const INLINE_RE = new RegExp(
  String.raw`(\*\*[^*\n]+\*\*|\`[^\`\n]+\`|\[[^\]]+\]\([^)]+\)|${BARE_URL_SOURCE}|(?:^|[^*])\*[^*\n]+\*(?!\*))`,
  "g",
);

/** Basic href scheme allowlist so LLM-authored links can't smuggle
 *  script URLs into the rich-copy HTML payload. */
function safeHref(href: string): string {
  return /^(https?:|mailto:)/i.test(href) ? href : "#";
}

function tokenizeInline(s: string): Inline[] {
  const out: Inline[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(INLINE_RE.source, "g");
  while ((match = re.exec(s)) !== null) {
    const token = match[0];
    // For italic (single *), the regex includes the preceding char so we
    // don't accidentally split **bold**. Slice past that anchor when we
    // detect italic.
    const italicIdx = token.search(/\*[^*\n]+\*(?!\*)/);
    const isUrl = /^https?:\/\//.test(token);
    const isItalic =
      italicIdx >= 0 &&
      !isUrl &&
      !token.startsWith("**") &&
      !token.startsWith("`") &&
      !token.startsWith("[");
    const tokenStart = match.index + (isItalic ? italicIdx : 0);
    if (tokenStart > lastIndex) {
      out.push({ kind: "text", text: s.slice(lastIndex, tokenStart) });
    }

    if (token.startsWith("**")) {
      out.push({ kind: "strong", text: token.slice(2, -2) });
    } else if (token.startsWith("`")) {
      out.push({ kind: "code", text: token.slice(1, -1) });
    } else if (token.startsWith("[")) {
      const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (m) out.push({ kind: "link", text: m[1]!, href: safeHref(m[2]!) });
    } else if (isUrl) {
      // Trailing sentence punctuation goes back into the text stream.
      const href = trimUrl(token);
      out.push({ kind: "link", text: linkLabel(href), href: safeHref(href) });
      if (href.length < token.length) {
        out.push({ kind: "text", text: token.slice(href.length) });
      }
    } else if (isItalic) {
      const emStart = token.indexOf("*", italicIdx);
      const inner = token.slice(emStart + 1, -1);
      out.push({ kind: "em", text: inner });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < s.length) {
    out.push({ kind: "text", text: s.slice(lastIndex) });
  }
  return out;
}

// ─── HTML renderer (for clipboard) ───────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineToHtml(tokens: Inline[]): string {
  return tokens
    .map((t) => {
      switch (t.kind) {
        case "text":
          return escapeHtml(t.text);
        case "strong":
          return `<strong>${escapeHtml(t.text)}</strong>`;
        case "em":
          return `<em>${escapeHtml(t.text)}</em>`;
        case "code":
          return `<code>${escapeHtml(t.text)}</code>`;
        case "link":
          return `<a href="${escapeHtml(t.href)}">${escapeHtml(t.text)}</a>`;
      }
    })
    .join("");
}

export function markdownToHtml(md: string): string {
  const blocks = parseBlocks(md);
  return blocks
    .map((b) => {
      if (b.kind === "heading") {
        return `<h${b.level}>${inlineToHtml(tokenizeInline(b.text))}</h${b.level}>`;
      }
      if (b.kind === "para") {
        const html = b.lines
          .map((ln) => inlineToHtml(tokenizeInline(ln)))
          .join("<br>");
        return `<p>${html}</p>`;
      }
      const items = b.items
        .map((i) => `<li>${inlineToHtml(tokenizeInline(i))}</li>`)
        .join("");
      return `<ul>${items}</ul>`;
    })
    .join("\n");
}

// ─── React renderer (for on-screen preview) ──────────────────────────

function inlineToReact(tokens: Inline[]): ReactNode[] {
  return tokens.map((t, i) => {
    switch (t.kind) {
      case "text":
        return <Fragment key={i}>{t.text}</Fragment>;
      case "strong":
        return <strong key={i}>{t.text}</strong>;
      case "em":
        return <em key={i}>{t.text}</em>;
      case "code":
        return <code key={i}>{t.text}</code>;
      case "link":
        return (
          <a
            key={i}
            href={t.href}
            target="_blank"
            rel="noopener noreferrer"
            className="content-link"
          >
            {t.text}
          </a>
        );
    }
  });
}

export function markdownToReact(md: string): ReactNode {
  const blocks = parseBlocks(md);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.kind === "heading") {
          const inline = inlineToReact(tokenizeInline(b.text));
          if (b.level === 1) return <h1 key={i}>{inline}</h1>;
          if (b.level === 2) return <h2 key={i}>{inline}</h2>;
          return <h3 key={i}>{inline}</h3>;
        }
        if (b.kind === "para") {
          return (
            <p key={i}>
              {b.lines.map((ln, k) => (
                <Fragment key={k}>
                  {k > 0 && <br />}
                  {inlineToReact(tokenizeInline(ln))}
                </Fragment>
              ))}
            </p>
          );
        }
        return (
          <ul key={i}>
            {b.items.map((item, j) => (
              <li key={j}>{inlineToReact(tokenizeInline(item))}</li>
            ))}
          </ul>
        );
      })}
    </>
  );
}
